import React, { useRef, useState, useEffect } from 'react';
import { Send, Smile, Loader2, X } from 'lucide-react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import MentionTextarea from '@/components/mentions/MentionTextarea';

interface CommentInputProps {
  isSubmitting: boolean;
  onSubmit: (text: string, mentions?: { full_name: string; auth_id: string }[]) => Promise<void>;
}

const CommentInput: React.FC<CommentInputProps> = React.memo(({ isSubmitting, onSubmit }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isDarkMode } = useTheme();
  const [text, setText] = useState('');
const [mentions, setMentions] = useState<{ full_name: string; auth_id: string }[]>([]);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || isSubmitting) return;
    try {
      await onSubmit(trimmed, mentions);
      setText('');
      setMentions([]);
    } catch (err) {
      console.error('Submit failed:', err);
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const textarea = document.getElementById('mention-textarea') as HTMLTextAreaElement;
    const cursorPos = textarea?.selectionStart ?? text.length;
    const before = text.slice(0, cursorPos);
    const after = text.slice(cursorPos);
    const updated = before + emojiData.emoji + after;
    setText(updated);
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const pos = cursorPos + emojiData.emoji.length;
        textarea.setSelectionRange(pos, pos);
      }
    }, 10);
    setShowEmojiPicker(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <form onSubmit={handleSubmit} className="relative flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowEmojiPicker(!showEmojiPicker);
          }}
          className={cn(
            'ml-2 transition-colors',
            showEmojiPicker ? 'text-gold-400' : 'text-dark-800 hover:text-gold-400'
          )}
        >
          <Smile className="h-5 w-5" />
        </button>

        <div className="flex-1 relative">
          <MentionTextarea
  id="mention-textarea"
  value={text}
  onChange={setText}
  onMentionsChange={setMentions}
  placeholder="Write a comment..."
  disabled={isSubmitting}
  className="bg-dark-300 border border-dark-400 rounded-2xl px-4 py-2.5 text-white placeholder-dark-800 w-full text-sm leading-tight"
/>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="mr-2 text-gold-400 hover:text-gold-500 disabled:text-dark-800 transition-colors"
        >
          {isSubmitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </button>
      </form>

      {showEmojiPicker && (
        <div
          className={cn(
            'absolute z-50 mt-2',
            'left-0 sm:left-auto sm:right-0',
            'w-full sm:w-auto'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <button
              onClick={() => setShowEmojiPicker(false)}
              className="absolute right-2 top-2 z-10 p-1 rounded-full bg-dark-300 text-white hover:bg-dark-400 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
              theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
              searchPlaceHolder="Search emoji..."
              width="100%"
              height={350}
              lazyLoadEmojis
              previewConfig={{ showPreview: false }}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
                maxWidth: '90vw',
                maxHeight: '80vh',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
});

CommentInput.displayName = 'CommentInput';
export default CommentInput;
